"use client"
import { useForm, SubmitHandler } from "react-hook-form";
import { Button } from "@ui/button";
import { Input } from "@ui/input";
import { toast } from "react-toastify";
import { Textarea } from "@ui/textarea";
import { Mail, Send, User, MessageSquare } from "lucide-react";

type FormData = {
  first_name: string;
  last_name: string;
  number: string;
  email: string;
  message: string;
};

export const ContactForm = () => {

  const form = useForm<FormData>();
  const { register, handleSubmit, formState, reset, setValue } = form;
  const { errors, isDirty, isSubmitting } = formState;

  const handleChange =
    (name: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const trimedValue = e.target.value.trim();
      setValue(name, trimedValue, { shouldValidate: true });
    };

  const submitForm: SubmitHandler<FormData> = async (data) => {
   
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const resData = await response.json();
      const statusCode = response.status;
      if (statusCode === 200) {
        reset();
        toast.success("Message sent successfully! We will respond shortly.");

      } else if (statusCode === 429) {
        reset();
        toast.warn(
          "Too many requests! You can submit only two request per day."
        );
  
      } else if (statusCode === 400) {
        toast.error("Bad request")
      } else {
        toast.error("Internal Server Error!")
      }
      return resData;
    } catch (error: any) {
      console.error(error);
      toast.error(`${error.data?.error_message || error.error}`);
      reset();
      toast.warn("Too many requests! You can submit only two request per day.");
    }
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} noValidate className="space-y-6">
      <div className="space-y-2">
        <label
          htmlFor="first_name"
          className="text-sm font-medium flex items-center gap-2"
        >
          <User className="w-4 h-4 text-healing-600" aria-hidden="true" />
          First name
        </label>
        <Input
          id="first_name"
          autoComplete="given-name"
          aria-invalid={!!errors.first_name}
          aria-describedby={errors.first_name ? "first_name-error" : undefined}
          {...register("first_name", {
            required: "Please enter first name!",
            pattern: {
              value: /^[a-zA-Z-']+$/,
              message: "Use only letters, hyphen or single quote",
            },
            validate: {
              tooLong: (fieldValue) => {
                return fieldValue.length < 31 || "Max of 30 letters!";
              },
              tooShort: (fieldValue) => {
                return fieldValue.length > 2 || "Min of 3 letters";
              },
            },
          })}
          placeholder="John"
          onChange={handleChange("first_name")}
          className="bg-white border-healing-200 focus:border-healing-400"
        />
        {errors.first_name && (
          <p id="first_name-error" role="alert" className="text-sm text-destructive">
            {errors.first_name.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <label
          htmlFor="last_name"
          className="text-sm font-medium flex items-center gap-2"
        >
          <User className="w-4 h-4 text-healing-600" aria-hidden="true" />
          Last Name
        </label>
        <Input
          id="last_name"
          autoComplete="family-name"
          aria-invalid={!!errors.last_name}
          aria-describedby={errors.last_name ? "last_name-error" : undefined}
          {...register("last_name", {
            required: "Please enter first name!",
            pattern: {
              value: /^[a-zA-Z-']+$/,
              message: "Use only letters, hyphen or single quote",
            },
            validate: {
              tooLong: (fieldValue) => {
                return fieldValue.length < 31 || "Max of 30 letters!";
              },
              tooShort: (fieldValue) => {
                return fieldValue.length > 2 || "Min of 3 letters";
              },
            },
          })}
          placeholder="Doe"
          onChange={handleChange("last_name")}
          className="bg-white border-healing-200 focus:border-healing-400"
        />
        {errors.last_name && (
          <p id="last_name-error" role="alert" className="text-sm text-destructive">
            {errors.last_name.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <label
          htmlFor="number"
          className="text-sm font-medium flex items-center gap-2"
        >
          <User className="w-4 h-4 text-healing-600" aria-hidden="true" />
          Phone number
        </label>
        <Input
          id="number"
          type="tel"
          autoComplete="tel"
          aria-invalid={!!errors.number}
          aria-describedby={errors.number ? "number-error" : undefined}
          {...register("number", {
            required: "Please enter phone number!",
            pattern: {
              value: /^\d+$/,
              message: "Please enter a valid phone number",
            },
            validate: {
              tooShort: (fieldValue) => {
                return (
                  fieldValue.toString().length > 9 ||
                  "Please enter a valid phone number"
                );
              },
            },
          })}
          placeholder="+2348098765432"
          onChange={handleChange("number")}
          className="bg-white border-healing-200 focus:border-healing-400"
        />
        {errors.number && (
          <p id="number-error" role="alert" className="text-sm text-destructive">
            {errors.number.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <label
          htmlFor="email"
          className="text-sm font-medium flex items-center gap-2"
        >
          <Mail className="w-4 h-4 text-healing-600" aria-hidden="true" />
          Email Address
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
          {...register("email", {
            required: "Please enter email!",
            pattern: {
              value:
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: "Please enter a valid email!",
            },
            validate: {
              notThisEmail: (fieldValue) => {
                return (
                  fieldValue != "admin@example.com" ||
                  "Please enter a different email"
                );
              },
              notBadEmail: (fieldValue) => {
                return (
                  !fieldValue.endsWith("@example.com") ||
                  "Please enter a different email"
                );
              },
            },
          })}
          placeholder="john@example.com"
          className="bg-white border-healing-200 focus:border-healing-400"
        />
        {errors.email && (
          <p id="email-error" role="alert" className="text-sm text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="message"
          className="text-sm font-medium flex items-center gap-2"
        >
          <MessageSquare className="w-4 h-4 text-healing-600" aria-hidden="true" />
          Message
        </label>
        <Textarea
          id="message"
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "message-error" : undefined}
          {...register("message", { required: "Message cannot be empty!" })}
          placeholder="Tell us more about your inquiry..."
          rows={6}
          className="bg-white border-healing-200 focus:border-healing-400 resize-none"
        />
        {errors.message && (
          <p id="message-error" role="alert" className="text-sm text-destructive">
            {errors.message.message}
          </p>
        )}
      </div>
    
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-healing-600 hover:bg-healing-700 text-white font-semibold py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

